import { selectArtifactContentForTab } from '../canvas/artifactDocumentView.js';

const firstTruthy = (...readers) => readers.reduce((result, read) => result || read(), undefined);

function object(value) {
  return value && typeof value === 'object' && !Array.isArray(value) ? value : {};
}

function text(value) {
  return String(value ?? '').trim();
}

function publicTitle(value) {
  const title = text(value);
  if (!title || ['canvas_manual_edit', 'manual_edit'].includes(title)) {
    return '';
  }
  return /^[a-z][a-z0-9]*(?:_[a-z0-9]+)+$/i.test(title) ? '' : title;
}

function tabLabel(activeTabKey = '', canvasTabLabelsByKey = {}) {
  const labels = object(canvasTabLabelsByKey);
  return text(labels[text(activeTabKey)]);
}

function latestRevision(document = {}) {
  return object(object(document).latest_revision);
}

function targetRefFromDocument({
  activeTabKey = '',
  document = {},
} = {}) {
  const revision = latestRevision(document);
  return {
    active_tab: text(activeTabKey) || text(document.active_tab) || 'requirement',
    artifact_ref: document.artifact_ref,
    artifact_id: text(document.artifact_id),
    revision_id: text(revision.revision_id),
    revision_key: text(revision.revision_key || revision.key),
    title: text(document.title),
    version: text(revision.version),
  };
}

function contextFromDocument(options) {
  const { activeTabKey, content, document } = { activeTabKey: '', content: '', document: {}, ...Object.fromEntries(Object.entries(Object(options)).filter(([, value]) => value !== undefined)) };
  const revision = latestRevision(document);
  return {
    artifact_id: text(document.artifact_id),
    document_kind: text(document.document_kind || document.artifact_type || document.kind),
    title: text(document.title),
    summary: text(content),
    content: text(content),
    content_format: text(document.content_format) || 'markdown',
    active_tab: text(activeTabKey) || text(document.active_tab) || 'requirement',
    status: text(document.status),
    revision_id: text(revision.revision_id),
    revision_key: text(revision.revision_key || revision.key),
    version: text(revision.version),
    source: 'canonical_canvas_active_tab',
    artifact_ref: document.artifact_ref,
  };
}

function chipFromContext(context = {}, canvasTabLabelsByKey = {}) {
  const title = publicTitle(context.title)
    || tabLabel(context.active_tab, canvasTabLabelsByKey)
    || '当前文档';
  return {
    label: title,
    title,
    activeTabKey: text(context.active_tab),
    artifactId: text(context.artifact_id),
  };
}

function analysisTarget(options) {
  const { activeTabKey, analysisPayload, canvasTabLabelsByKey } = { activeTabKey: '', analysisPayload: {}, canvasTabLabelsByKey: {}, ...Object.fromEntries(Object.entries(Object(options)).filter(([, value]) => value !== undefined)) };
  const payload = object(analysisPayload);
  const document = object(payload.document);
  const content = text(firstTruthy(() => (payload.markdown), () => (payload.content)));
  if (!content || !payload.artifact_ref || !text(document.artifact_id)) {
    return null;
  }
  const normalizedDocument = {
    ...document,
    artifact_id: text(document.artifact_id),
    artifact_ref: payload.artifact_ref,
    artifact_type: firstTruthy(() => (text(document.artifact_type)), () => ('analysis_report')),
    title: firstTruthy(() => (publicTitle(document.title)), () => (tabLabel(activeTabKey || 'analysis', canvasTabLabelsByKey)), () => ('当前文档')),
    content_format: 'markdown',
    status: text(document.status) || text(payload.status),
  };
  const context = contextFromDocument({
    activeTabKey: firstTruthy(() => (activeTabKey), () => ('analysis')),
    content,
    document: normalizedDocument,
  });
  return { chip: chipFromContext(context, canvasTabLabelsByKey), context, targetRef: targetRefFromDocument({ activeTabKey: context.active_tab, document: normalizedDocument }) };
}

function intakeTarget(options) {
  const { activeTabKey, canvasTabLabelsByKey, intakeCanvasState } = { activeTabKey: '', canvasTabLabelsByKey: {}, intakeCanvasState: {}, ...Object.fromEntries(Object.entries(Object(options)).filter(([, value]) => value !== undefined)) };
  const state = object(intakeCanvasState);
  const document = object(state.artifact_document);
  const tabKey = text(activeTabKey) || text(document.active_tab) || 'requirement';
  const content = selectArtifactContentForTab({
    artifactDocument: document,
    fallbackContent: text(state.content || state.canvas_content),
    tabKey,
    versions: Array.isArray(state.versions) ? state.versions : [],
  });
  if (!text(document.artifact_id) || !text(content)) {
    return null;
  }
  const context = contextFromDocument({ activeTabKey: tabKey, content, document });
  return { chip: chipFromContext(context, canvasTabLabelsByKey), context, targetRef: targetRefFromDocument({ activeTabKey: tabKey, document }) };
}

export function resolveComposerActiveArtifactTarget(options) {
  const { canvasActiveTabKey, canvasTabLabelsByKey, canvasAnalysisPayload, intakeCanvasState, showCanvasPanel } = { canvasActiveTabKey: '', canvasTabLabelsByKey: {}, canvasAnalysisPayload: null, intakeCanvasState: {}, showCanvasPanel: false, ...Object.fromEntries(Object.entries(Object(options)).filter(([, value]) => value !== undefined)) };
  if (showCanvasPanel !== true) {
    return null;
  }
  const activeTabKey = text(canvasActiveTabKey);
  if (activeTabKey === 'analysis') {
    return analysisTarget({ activeTabKey, analysisPayload: object(canvasAnalysisPayload), canvasTabLabelsByKey });
  }
  return intakeTarget({ activeTabKey, canvasTabLabelsByKey, intakeCanvasState });
}

export function resolveComposerActiveArtifactTargetKey(target = {}) {
  const item = object(target);
  const context = object(item.context);
  const targetRef = object(item.targetRef);
  return [
    text(targetRef.artifact_id) || text(context.artifact_id),
    text(targetRef.active_tab) || text(context.active_tab),
    text(targetRef.revision_id) || text(context.revision_id),
    text(targetRef.revision_key) || text(context.revision_key),
  ].filter(Boolean).join('::');
}

export default resolveComposerActiveArtifactTarget;
